import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";

const timeline = [
  { year: "2019", title: "品牌创立", desc: "球幕影院品牌在上海成立，提出「走进电影」的沉浸观影理念。" },
  { year: "2021", title: "首座穹顶厅落成", desc: "一号穹顶厅正式开放，21 米半球银幕迎来第一批观众。" },
  { year: "2023", title: "激光放映升级", desc: "全线升级 8K 激光融合投影与 12.1 声道系统，画质与声场同步跃升。" },
  { year: "2025", title: "国际合作启动", desc: "与海外自然影像团队合作，引进多部穹顶专属纪录片。" },
  { year: "2026", title: "三厅全面运营", desc: "二号、三号穹顶厅投入运营，形成科幻、纪录、艺术三大主题体系。" },
];

const team = [
  {
    image:
      "https://readdy.ai/api/search-image?query=Professional%20studio%20portrait%20of%20a%20confident%20middle%20aged%20man%20in%20a%20dark%20blazer%2C%20deep%20navy%20background%20with%20soft%20amber%20rim%20light%2C%20corporate%20editorial%20photography%2C%20high%20detail%2C%20clean%20consistent%20style&width=600&height=600&seq=qiubu-team-01&orientation=squarish",
    name: "沈以宁",
    role: "创始人 · 总策展人",
  },
  {
    image:
      "https://readdy.ai/api/search-image?query=Professional%20studio%20portrait%20of%20a%20friendly%20young%20woman%20with%20short%20hair%20in%20a%20dark%20knit%2C%20deep%20navy%20background%20with%20soft%20amber%20rim%20light%2C%20corporate%20editorial%20photography%2C%20high%20detail%2C%20clean%20consistent%20style&width=600&height=600&seq=qiubu-team-02&orientation=squarish",
    name: "林可",
    role: "放映技术总监",
  },
  {
    image:
      "https://readdy.ai/api/search-image?query=Professional%20studio%20portrait%20of%20a%20calm%20man%20with%20glasses%20in%20a%20dark%20shirt%2C%20deep%20navy%20background%20with%20soft%20amber%20rim%20light%2C%20corporate%20editorial%20photography%2C%20high%20detail%2C%20clean%20consistent%20style&width=600&height=600&seq=qiubu-team-03&orientation=squarish",
    name: "陈默",
    role: "内容与合作负责人",
  },
  {
    image:
      "https://readdy.ai/api/search-image?query=Professional%20studio%20portrait%20of%20a%20warm%20smiling%20woman%20in%20a%20dark%20blazer%2C%20deep%20navy%20background%20with%20soft%20amber%20rim%20light%2C%20corporate%20editorial%20photography%2C%20high%20detail%2C%20clean%20consistent%20style&width=600&height=600&seq=qiubu-team-04&orientation=squarish",
    name: "周雅",
    role: "客户体验主管",
  },
];

const advantages = [
  { icon: "ri-map-pin-2-line", title: "核心地段", desc: "位于浦东穹顶艺术中心，地铁直达，交通便利。" },
  { icon: "ri-cpu-line", title: "顶级设备", desc: "8K 激光融合投影与 12.1 声道，硬件全国领先。" },
  { icon: "ri-award-line", title: "专属片源", desc: "多部影片为穹顶空间专属制作，仅在本馆呈现。" },
  { icon: "ri-shield-check-line", title: "舒适体验", desc: "环绕阶梯座椅、恒温恒湿，长时间观影亦舒适。" },
];

export default function About() {
  usePageMeta(
    "关于我们-球幕影院品牌与团队介绍",
    "了解球幕影院的品牌故事、发展历程、使命愿景与运营团队，以及场馆位置与设备优势。",
    "关于球幕影院,品牌介绍,发展历程,运营团队,沉浸式影院",
  );
  return (
    <div className="w-full pt-20">
      <section className="bg-background-100/60 border-b border-background-200/60">
        <div className="max-w-content mx-auto px-4 md:px-8 py-12 md:py-16">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">关于我们</span>
          </nav>
          <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950 mb-4">关于球幕影院</h1>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
            我们相信，最好的故事不该被银幕的边框限制。球幕影院致力于用穹顶科技，让观众真正走进影像之中。
          </p>
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <div className="rounded-lg overflow-hidden aspect-[4/3] bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Exterior%20view%20of%20a%20modern%20dome%20shaped%20cinema%20building%20at%20dusk%20with%20warm%20glowing%20windows%2C%20deep%20navy%20sky%2C%20architectural%20photography%2C%20high%20detail%2C%20premium%20cinematic%20atmosphere%2C%20clean%20composition&width=1200&height=900&seq=qiubu-about-01&orientation=landscape"
                alt="球幕影院建筑外观"
                title="球幕影院穹顶艺术中心外观"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div>
              <p className="text-xs font-label tracking-widest text-accent-500 uppercase mb-3">Our Story</p>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-5">
                从一块穹顶开始的旅程
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                2019 年，我们在一间小放映室里实验半球投影的可能性。那一年，观众第一次抬起头，
                看到星空在头顶流动——从那一刻起，我们决定把「沉浸」这件事做到极致。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                如今，球幕影院拥有三座专业穹顶放映厅，与国内外影像团队保持长期合作，
                持续为电影爱好者带来无法在普通影厅复制的观影体验。
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-background-100/60 border-y border-background-200/60">
        <div className="max-w-content mx-auto px-4 md:px-8 py-16 md:py-20">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-10">发展历程</h2>
          <div className="relative pl-6 md:pl-8">
            <div className="absolute left-0 top-1 bottom-1 w-px bg-background-300/60" />
            <div className="flex flex-col gap-8">
              {timeline.map((item, i) => (
                <Reveal key={item.year} delay={i * 70}>
                  <div className="relative">
                    <span className="absolute -left-6 md:-left-8 top-1 w-3 h-3 rounded-full bg-accent-500 -translate-x-1/2" />
                    <span className="font-heading font-bold text-accent-500 text-lg">{item.year}</span>
                    <h3 className="font-heading font-semibold text-foreground-950 mt-1 mb-1.5">{item.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full p-8 rounded-lg bg-primary-500 text-background-50">
              <i className="ri-focus-3-line text-3xl mb-5 block" />
              <h2 className="font-heading font-bold text-xl md:text-2xl mb-4">我们的使命</h2>
              <p className="text-sm text-background-50/85 leading-relaxed">
                让每一位观众都能用身体去感受故事，而不只是用眼睛去看。
                用沉浸技术降低想象的门槛，让科学、自然与艺术触手可及。
              </p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="h-full p-8 rounded-lg bg-background-100 border border-background-200/70">
              <i className="ri-eye-line text-3xl mb-5 block text-accent-500" />
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">我们的愿景</h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                成为国内最受信赖的穹顶沉浸观影品牌，让球幕影院成为城市里最值得反复造访的文化空间。
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-background-100/60 border-y border-background-200/60">
        <div className="max-w-content mx-auto px-4 md:px-8 py-16 md:py-20">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-10">运营团队</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {team.map((member, i) => (
              <Reveal key={member.name} delay={i * 80}>
                <div className="rounded-lg overflow-hidden bg-background-50 border border-background-200/70">
                  <div className="aspect-square overflow-hidden bg-background-200">
                    <img
                      src={member.image}
                      alt={`${member.name} 球幕影院${member.role}`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-4 text-center">
                    <h3 className="font-heading font-semibold text-foreground-950">{member.name}</h3>
                    <p className="text-xs text-foreground-500 mt-1">{member.role}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-8 py-16 md:py-20">
        <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-8">位置与设备优势</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {advantages.map((item, i) => (
            <Reveal key={item.title} delay={i * 70}>
              <div className="h-full p-5 rounded-lg bg-background-100 border border-background-200/70">
                <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-500/15 text-accent-500 text-xl mb-4">
                  <i className={item.icon} />
                </span>
                <h3 className="font-heading font-semibold text-foreground-950 mb-1.5">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}